import { getNowBasedOnBooking } from '@lib/booking';
import {
  getEarliestStartDateWithOriginalTimezone,
  getLatestEndDateWithOriginalTimezone,
} from '@lib/trip';
import { DateTime } from 'luxon';

import type { PhasedBooking } from '@lib/phased-booking';

export enum Phase {
  BeforeJourney,
  BeforeOnlineCheckIn,
  BeforePresenceCheckIn,
  OnFirstDay,
  OnSecondDay,
  OnJourney,
  LastThreeDaysOfJourney,
  AfterJourney,
}

type PhaseDefinition = {
  id: Phase;
  name: string;
  description: string;
};
export const phaseDefinitions: PhaseDefinition[] = [
  {
    id: Phase.BeforeJourney,
    name: 'Phase 0 | Before Journey',
    description: 'more than 4 months before journey',
  },
  {
    id: Phase.BeforeOnlineCheckIn,
    name: 'Phase 1 | Before Online CheckIn',
    description: 'less than 4 months and more than 21 days until journey',
  },
  {
    id: Phase.BeforePresenceCheckIn,
    name: 'Phase 2 | Before Presence CheckIn',
    description: '21 days or less until journey',
  },
  {
    id: Phase.OnFirstDay,
    name: 'Phase 3 | On First Day',
    description: 'first day of journey',
  },
  {
    id: Phase.OnSecondDay,
    name: 'Phase 4 | On Second Day',
    description: 'second day of journey',
  },
  {
    id: Phase.OnJourney,
    name: 'Phase 5 | On Journey',
    description: 'on journey',
  },
  {
    id: Phase.LastThreeDaysOfJourney,
    name: 'Phase 6 | Last Three Days Of Journey',
    description: 'last three days of journey',
  },
  {
    id: Phase.AfterJourney,
    name: 'Phase 7 | After Journey',
    description: 'after journey',
  },
];

export const PHASES_BEFORE_JOURNEY: Phase[] = [
  Phase.BeforeJourney,
  Phase.BeforeOnlineCheckIn,
  Phase.BeforePresenceCheckIn,
];

export const PHASES_ON_JOURNEY: Phase[] = [
  Phase.OnFirstDay,
  Phase.OnSecondDay,
  Phase.OnJourney,
  Phase.LastThreeDaysOfJourney,
];

export const PHASES_AFTER_JOURNEY: Phase[] = [Phase.AfterJourney];

export const isValidPhase = (potentialPhase: any): potentialPhase is Phase => {
  if (
    typeof potentialPhase !== 'string' &&
    typeof potentialPhase !== 'number'
  ) {
    return false;
  }

  const asNumber =
    typeof potentialPhase === 'string'
      ? parseInt(potentialPhase, 10)
      : potentialPhase;
  const ids = phaseDefinitions.reduce<Phase[]>(
    (collection, phaseDefinition) => {
      return [...collection, phaseDefinition.id];
    },
    [],
  );

  return ids.includes(asNumber as unknown as Phase);
};

export const getCurrentPhaseOrDefault = (booking: PhasedBooking): Phase => {
  return getCurrentPhase(booking) ?? Phase.BeforeJourney;
};

export const getCurrentPhase = (booking: PhasedBooking): Phase | undefined => {
  const from = getEarliestStartDateWithOriginalTimezone(booking);
  const to = getLatestEndDateWithOriginalTimezone(booking);

  if (typeof from === 'undefined' || typeof to === 'undefined') {
    return undefined;
  }

  const calculationArgs: PhaseCalculationArgs = {
    now: getNowBasedOnBooking(booking),
    from,
    to,
    checkedIn: booking.checkedIn,
  };

  const [currentTrip] = booking.trips;

  if (currentTrip?.stages && currentTrip?.stages?.length > 1) {
    const firstStage = currentTrip.stages[0];
    const lastStage = currentTrip.stages[currentTrip.stages.length - 1];

    if (firstStage.arrivalDateTime) {
      calculationArgs.shipArrivalTimeToStart = DateTime.fromISO(
        firstStage.arrivalDateTime,
        { setZone: true },
      );
    }
    if (lastStage.arrivalDateTime) {
      calculationArgs.shipArrivalTimeToEnd = DateTime.fromISO(
        lastStage.arrivalDateTime,
        { setZone: true },
      );
    }
  }

  return calculateCurrentPhaseFromDates(calculationArgs);
};

const matchPhase_doNotImport = (phaseToMatch: Phase, collection: Phase[]) => {
  return collection.includes(phaseToMatch);
};

export const phaseIsOnJourney = (phase: Phase): boolean => {
  return matchPhase_doNotImport(phase, PHASES_ON_JOURNEY);
};

export const phaseIsBeforeJourney = (phase: Phase): boolean => {
  return matchPhase_doNotImport(phase, PHASES_BEFORE_JOURNEY);
};

export const phaseIsAfterJourney = (phase: Phase): boolean => {
  return matchPhase_doNotImport(phase, PHASES_AFTER_JOURNEY);
};

export const getPhaseDefinition = (
  phase: Phase,
): (typeof phaseDefinitions)[number] | undefined => {
  return phaseDefinitions.find(def => {
    return def.id === phase;
  });
};

//#region Phase Calculation
export type PhaseCalculationArgs = {
  now: DateTime;
  from: DateTime;
  to: DateTime;
  checkedIn?: boolean | 'auto';
  shipArrivalTimeToStart?: DateTime;
  shipArrivalTimeToEnd?: DateTime;
};
type ShipTimes = 'shipArrivalTimeToStart' | 'shipArrivalTimeToEnd';
type PhaseCalculationArgsWithShipTimes = Omit<PhaseCalculationArgs, ShipTimes> &
  Required<Pick<PhaseCalculationArgs, ShipTimes>>;

export const determineCheckedInStatus = (
  args: PhaseCalculationArgsWithShipTimes,
): boolean => {
  const { now, from, to, checkedIn } = args;
  const nowIsBetweenFromAndTo = now >= from && now <= to;
  const shipArrivalTimeToStart =
    args.shipArrivalTimeToStart || from.startOf('day'); // fallback to start of day if not provided
  const shipArrivalTimeToEnd = args.shipArrivalTimeToEnd || to.startOf('day'); // fallback to start of day if not provided

  // Now is before ship arrival in port or after end of journey day
  if (now < shipArrivalTimeToStart || now > to.endOf('day')) {
    return false;
  }

  // Ship is in port and user is possible to check in
  // If we have data or it's automatically calculated return true
  if (checkedIn === true || (checkedIn === 'auto' && nowIsBetweenFromAndTo)) {
    return true;
  }

  // Journey didn't start and user is not checked in or we don't know
  if (!checkedIn && now < from) {
    return false;
  }

  // Special case: one day trip
  if (from.hasSame(to, 'day')) {
    // Is a one day trip

    // User isn't checked in but journey already started
    if (checkedIn === false && now >= from && now < to) {
      return true;
    }

    // We don't know check-in status but journey already started
    if (checkedIn === undefined && now >= from) {
      return true;
    }
  } else {
    // Proceed with regular logic for multi-day trips

    // User already checked out
    if (checkedIn === false && now > shipArrivalTimeToEnd) {
      return false;
    }

    // User isn't checked in but journey already started
    if (!checkedIn && now >= from) {
      return true;
    }
  }

  return false;
};

/**
 * Calculates the current travel phase (Phase) based on start/end date, current time, and check-in status.
 *
 * This function models complex transitions between different phases of a journey, including:
 * - Before the journey (more than 4 months in advance)
 * - Before online check-in (between 4 months and 21 days in advance)
 * - Before presence check-in (21 days or less before the journey, up to journey start)
 * - On the first day (only if checked in, otherwise remains "before presence check-in")
 * - On the second day (for multi-day trips)
 * - On the journey
 * - Last three days of the journey
 * - After the journey
 *
 * Special features:
 * - Considers check-in and check-out as well as an "auto" mode, which determines the status automatically depending on whether the current time is between start and end.
 * - Handles 1-day, 2-day, and multi-day trips with special logic for phase transitions (see tests for edge cases).
 * - If check-in status is unknown, a plausible state is assumed based on the current day and position relative to the journey.
 * - The function is robust for all times before, during, and after the journey, and also handles edge cases such as "just after the end" correctly.
 *
 * @param args Object with the following properties:
 *   - now: Current time (Luxon DateTime)
 *   - from: Start time of the journey (Luxon DateTime)
 *   - to: End time of the journey (Luxon DateTime)
 *   - checkedIn: Optional check-in status (true, false, 'auto', or undefined)
 *   - shipArrivalTimeBeforeStart: Optional ship arrival time before journey start
 * @returns The calculated phase as a value of the Phase enum
 * @see https://tuicruises.atlassian.net/browse/OM-2089?focusedCommentId=114420
 */
export const calculateCurrentPhaseFromDates = (
  args: PhaseCalculationArgs,
): Phase => {
  const {
    now,
    from,
    to,
    checkedIn,
    shipArrivalTimeToStart = args.shipArrivalTimeToStart || from.startOf('day'), // fallback to start of day if not provided
    shipArrivalTimeToEnd = args.shipArrivalTimeToEnd || to.startOf('day'), // fallback to start of day if not provided
  } = args;

  // Determine effective check-in state
  const effectiveCheckedIn: boolean = determineCheckedInStatus({
    ...args,
    shipArrivalTimeToStart,
    shipArrivalTimeToEnd,
  });

  // Now start with calculating phases
  // Not in chronological order for easier reading of each phase

  // Phase 0: Before Journey; more than 4 months before journey
  const phase0_calendarCalculated = from.minus({ months: 4 });

  // Calculate the difference in offsets (in minutes)
  // e.g. April (from) is +120 mins (UTC+2); December (phase0_calendarCalculated) is +60 mins (UTC+1)
  const offsetDiffPhase0 = from.offset - phase0_calendarCalculated.offset;

  // We add/subtract the difference to maintain the same UTC moment
  const phase0_dstAdjusted = phase0_calendarCalculated.minus({
    minutes: offsetDiffPhase0,
  });

  if (now < phase0_dstAdjusted) {
    return Phase.BeforeJourney;
  }

  // Phase 1: Before Online Check-In; between 4 months and 21 days before journey
  // WARNING: In order to respect DST changes, you have to use hours or smaller units!
  //          Otherwise, the DST shift won't be respected!
  if (now < from.minus({ hours: 21 * 24 })) {
    return Phase.BeforeOnlineCheckIn;
  }

  // Phase 7: After Journey
  if (
    now > to.endOf('day') ||
    (!effectiveCheckedIn && now > shipArrivalTimeToEnd && now > from)
  ) {
    return Phase.AfterJourney;
  }

  // Phase 3: On First Day
  if (effectiveCheckedIn && now.hasSame(from, 'day')) {
    return Phase.OnFirstDay;
  }

  // Phase 2: Before Presence Check-In; within 21 days before journey and start of journey
  // Split up for better readability
  if (
    // Regular case
    !from.hasSame(to, 'day') &&
    ((!effectiveCheckedIn && now <= from.endOf('day')) ||
      (effectiveCheckedIn && now < shipArrivalTimeToStart))
  ) {
    return Phase.BeforePresenceCheckIn;
  } else if (
    // Special case for 1-day-trip where from and to are on same day
    from.hasSame(to, 'day') &&
    // We don't know check-in status
    ((checkedIn === undefined && now < to) ||
      // We know user is checked out
      (checkedIn === 'auto' && now < from && !effectiveCheckedIn) ||
      // We know user is not checked in
      (checkedIn === false &&
        now < from.endOf('day') &&
        now < to &&
        !effectiveCheckedIn) ||
      // We know user is checked in
      (checkedIn === true && now < shipArrivalTimeToStart))
  ) {
    return Phase.BeforePresenceCheckIn;
  }

  // Phase 6: Last Three Days Of Journey
  if (
    // Can't be this phase on first day on journey
    now > from.endOf('day') &&
    // Is within last three days
    // WARNING: In order to respect DST changes, you have to use hours or smaller units!
    //          Otherwise, the DST shift won't be respected!
    now >= to.startOf('day').minus({ hours: 2 * 24 }) // last three days include the end day
  ) {
    return Phase.LastThreeDaysOfJourney;
  }

  // Phase 4: On Second Day; only for trips of at least two days
  // WARNING: In order to respect DST changes, you have to use hours or smaller units!
  //          Otherwise, the DST shift won't be respected!
  if (now > from.endOf('day') && now < from.plus({ hours: 24 }).endOf('day')) {
    return Phase.OnSecondDay;
  }

  return Phase.OnJourney;
};
//#endregion
